import React, { createRef } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import _ from 'lodash';

import emitter from '../../emitter';
import {
  IFRAME_RESIZED,
  CONF_MENU_CHANGE,
  SCREENSAVER_STARTED,
} from '../../settings/events';
import { POINTER_DOWN } from '../../util/supportedEvents';
import { innerWidth } from '../../util/dom';
import Portal from '../utils/Portal';
import StopPropagation from '../utils/StopPropagation';
import TransitionTarget from '../Transition/TransitionTarget';
import { states } from '../Transition/transitionStates';
import platform from '../../util/platform';

import setPosition from './setPosition';
import * as alignments from './alignments';
import styles from './Popup.module.less';
import Outline from '../Outline';
import constants from '../../settings/constants';
import { selectScreensaverMode } from '../../state/screensaver/screensaverSelectors';

const supportsMaxContentWidth =
  typeof CSS === 'undefined' ? false : CSS.supports('width', 'max-content');

class Popup extends TransitionTarget {
  static propTypes = {
    alignment: PropTypes.oneOf(alignments.all),
    autoWidth: PropTypes.bool,
    calculateTargetRect: PropTypes.func,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    dismissible: PropTypes.bool,
    autoUpdate: PropTypes.bool,
    margin: PropTypes.number,
    onClose: PropTypes.func,
    outlineTarget: PropTypes.bool,
    spacing: PropTypes.number,
    target: PropTypes.any,
    targetRect: PropTypes.object,
    timeout: PropTypes.number,
    wiggle: PropTypes.bool,
    maxLines: PropTypes.number,
    screensaverMode: PropTypes.string,
    ignorePadding: PropTypes.bool,
    arrowAlignment: PropTypes.string,
  };

  static defaultProps = {
    alignment: alignments.LEFT,
    dismissible: true,
    margin: 5,
    spacing: 10,
    autoWidth: false,
  };

  //
  // Local variables

  _root = createRef();
  _container = createRef();
  _contentHolder = createRef();

  classNames = styles;
  state = {
    shownOnce: false,
    hasCalculatedPosition: false,
    hasCalculatedOutline: false,
    alignment: null,
  };

  constructor(...args) {
    super(...args);

    this._calculateStyles = this._calculateStyles.bind(this);
    this._calculateOutline = this._calculateOutline.bind(this);
    this._onConfChanged = this._onConfChanged.bind(this);
  }

  componentDidMount() {
    // TODO set in proptypes that onClose needs to be set if timeout is set
    if (this.props.timeout && this.props.onClose) {
      setTimeout(this.props.onClose, this.props.timeout);
    }

    if (this.props.dismissible) {
      window.addEventListener(POINTER_DOWN, this.onPointerDown);
    }

    this._calculateStyles();

    if (this.props.autoWidth && !supportsMaxContentWidth) {
      this.observer = new MutationObserver(() => this._calculateStyles());

      this.observer.observe(this._container.current, {
        childList: true,
        subtree: true,
      });
    }

    emitter.on(IFRAME_RESIZED, this._calculateStyles);

    // on update give the browser some time to update so we get correct measurments
    emitter.on(CONF_MENU_CHANGE, this._onConfChanged);
    emitter.on(SCREENSAVER_STARTED, this.onPointerDown);
  }

  componentWillUnmount() {
    emitter.off(IFRAME_RESIZED, this._calculateStyles);
    emitter.off(CONF_MENU_CHANGE, this._onConfChanged);
    emitter.off(SCREENSAVER_STARTED, this.onPointerDown);

    if (this.props.dismissible) {
      window.removeEventListener(POINTER_DOWN, this.onPointerDown);
    }

    if (this.observer) {
      this.observer.disconnect();
    }
  }

  componentDidUpdate(prevProps) {
    const rect = this._findRect();
    if (!rect && !this.state.destroyed) {
      this.setState({
        destroyed: true,
      });
    }

    if (this.props.autoUpdate) {
      if (prevProps.content !== this.props.content) {
        this._calculateStyles();
      } else if (prevProps.transitionState === states.entered) {
        this._calcSize(rect);
      }
    }
  }

  //
  // Control

  _calcSize(rect) {
    const { alignment, margin, spacing } = this.props;

    if (this._container.current) {
      const root = findDOMNode(this._root.current);
      const contentHolder = this._contentHolder.current;

      root.style.transform = 'none';

      if (!this.props.autoWidth) {
        const startWidth = platform.isKiosk ? 340 : 240;
        const maxLines = this.props.maxLines || 3;
        const computedStyle = window.getComputedStyle(contentHolder);
        const originalPaddingString = computedStyle.getPropertyValue('padding');
        const originalPaddingLeftNum = parseInt(
          computedStyle.getPropertyValue('padding-left')
        );
        const originalPaddingRightNum = parseInt(
          computedStyle.getPropertyValue('padding-right')
        );
        const lineHeightNum = parseInt(
          computedStyle.getPropertyValue('line-height')
        );

        /*
        Set padding and icon (if there is one) height to zero,
        so that it won't conflict with the calculation
        */

        contentHolder.style.padding = 0;

        const iconElement = root.querySelector('.' + styles.icon);
        let originalIconHeight;

        if (iconElement) {
          originalIconHeight = iconElement.offsetHeight;
          iconElement.style.height = 0;
        }

        contentHolder.style.width = `${startWidth}px`;

        //Calculate the smallest width needed to fit the text content.
        const tightedTextContentWidth = this.getTightestPossibleWidth(
          contentHolder,
          lineHeightNum,
          maxLines
        );

        //Set new width, with added space for padding
        contentHolder.style.width =
          tightedTextContentWidth +
          originalPaddingLeftNum +
          originalPaddingRightNum +
          'px';

        //Reset padding and icon height to their original values.
        contentHolder.style.padding = originalPaddingString;

        if (iconElement) {
          iconElement.style.height = originalIconHeight + 'px';
        }
      } else if (!supportsMaxContentWidth) {
        const computedStyle = window.getComputedStyle(contentHolder);
        const padding =
          parseInt(computedStyle.getPropertyValue('padding-left')) +
          parseInt(computedStyle.getPropertyValue('padding-right'));
        const border =
          parseInt(computedStyle.getPropertyValue('border-left-width')) +
          parseInt(computedStyle.getPropertyValue('border-right-width'));
        contentHolder.style.width = `${
          innerWidth(contentHolder.firstChild) + padding + border
        }px`;
      }

      root.style.transform = 'none';

      const size = contentHolder.getBoundingClientRect();

      root.style.transform = null;

      const result = setPosition(size, rect, {
        alignment,
        margin,
        spacing,
      });

      return result;
    }
  }

  getTightestPossibleWidth(element, lineHeight, maxLines) {
    const maxWidth = Math.round(0.7 * window.innerWidth);
    const minWidth = 10;

    function widenToFitAllText() {
      while (
        (element.clientHeight > lineHeight * maxLines ||
          element.scrollWidth > element.offsetWidth) &&
        element.offsetWidth <= maxWidth - 10
      ) {
        element.style.width = element.offsetWidth + 10 + 'px';
      }
    }
    function getTightenedWidth() {
      const currentHeight = element.clientHeight;
      let previousWidth;

      do {
        previousWidth = element.offsetWidth;
        element.style.width = element.offsetWidth - 2 + 'px';
      } while (
        element.clientHeight === currentHeight &&
        element.offsetWidth >= element.scrollWidth &&
        element.offsetWidth >= minWidth
      );

      return previousWidth;
    }

    widenToFitAllText();
    return getTightenedWidth();
  }

  _findRect() {
    const { calculateTargetRect, targetRect, target, ignorePadding } =
      this.props;

    let rect;
    if (targetRect) {
      rect = targetRect;
    } else if (calculateTargetRect) {
      rect = calculateTargetRect();
    } else if (target) {
      const DOMNode = target instanceof Element ? target : findDOMNode(target);
      rect = this._getBoundingClientRectClone(DOMNode);
      if (ignorePadding) {
        const computedStyle = window.getComputedStyle(DOMNode);
        rect.width -=
          parseInt(computedStyle.getPropertyValue('padding-left')) +
          parseInt(computedStyle.getPropertyValue('padding-right'));
        rect.left += parseInt(computedStyle.getPropertyValue('padding-left'));
        rect.height -=
          parseInt(computedStyle.getPropertyValue('padding-top')) +
          parseInt(computedStyle.getPropertyValue('padding-bottom'));
        rect.top += parseInt(computedStyle.getPropertyValue('padding-top'));
      }
    } else {
      throw Error(
        'You need to pass target, targetRect or calculateTargetRect to Popup'
      );
    }
    return rect;
  }

  _getBoundingClientRectClone(element) {
    const rect = element.getBoundingClientRect();
    return {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      x: rect.x,
      y: rect.y,
    };
  }

  _onConfChanged() {
    this._calculateOutline();
    _.delay(() => this._calculateStyles(), 200);
  }

  // this has not been converted to getDerivedStateFromProps because we need reference to the ref
  _calculateStyles() {
    // Calculate the width
    if (this._container.current) {
      const rect = this._findRect();
      // Sprite was destroyed, don't bother
      if (!rect) {
        return;
      }
      const result = this._calcSize(rect);
      const style = {
        position: 'fixed',
        left: Math.round(result.left) + 'px',
        top: Math.round(result.top) + 'px',
      };

      const contentStyle = {
        left: result.adjust.left && result.adjust.left + 'px',
        top: result.adjust.top && result.adjust.top + 'px',
      };
      const outlineState = this._calculateOutline(rect);
      this.setState({
        ...outlineState,
        targetRect: rect,
        style,
        contentStyle,
        alignment: result.alignment,
        hasCalculatedPosition: true,
      });
    }
  }

  _calculateOutline(rect = null) {
    const state = {
      hasCalculatedOutline: true,
    };
    if (rect) {
      return state;
    }
    if (this._container.current) {
      rect = this._findRect();
      // Sprite was destroyed, don't bother
      if (!rect) {
        return;
      }
      state.targetRect = rect;
      this.setState(state);
    }
  }

  onPointerDown = event => {
    if (this.props.screensaverMode !== constants.SCREENSAVER_MODE.countdown) {
      this.props.onClose && this.props.onClose();
    }
  };

  render() {
    const {
      className,
      outlineTarget,
      children,
      onClose,
      transitionState,
      wiggle,
      arrowAlignment,
    } = this.props;

    const {
      alignment,
      contentStyle,
      hasCalculatedPosition,
      hasCalculatedOutline,
      style,
      targetRect,
      destroyed,
    } = this.state;

    if (destroyed) {
      return null;
    }

    return (
      <React.Fragment>
        <Portal>
          <StopPropagation
            ref={this._root}
            style={style}
            className={classNames(
              styles.popup,
              !hasCalculatedPosition && styles.invisible,
              arrowAlignment && styles[`arrow-${arrowAlignment}`],
              alignment && styles[`${alignment}-aligned`],
              wiggle && styles.wiggle,
              className,
              this.getTransitionClassNames()
            )}
          >
            <div
              onClick={this.onPointerDown}
              ref={this._container}
              className={styles.container}
            >
              <div
                ref={this._contentHolder}
                className={classNames(styles.contentHolder)}
                style={contentStyle}
              >
                {children}
              </div>

              <div className={styles.arrow} />
            </div>
          </StopPropagation>
        </Portal>

        {hasCalculatedOutline && outlineTarget && (
          <Outline
            transitionState={transitionState}
            targetRect={targetRect}
            onClose={onClose}
          />
        )}
      </React.Fragment>
    );
  }
}

export default connect(state => ({
  screensaverMode: selectScreensaverMode(state),
}))(Popup);
